import React from 'react';
import { ArrowUpRight, ChevronDown, ChevronUp, EyeOff, Sparkles, Wrench } from 'lucide-react';
import type { AssistantTool, AssistantToolGroupLabel } from './types';

export interface AssistantToolToolbarProps {
  routeLabel: string;
  tools: AssistantTool[];
  isCollapsed: boolean;
  hiddenAll: boolean;
  onToggleCollapsed: () => void;
  onHideTool: (toolId: string) => void;
  onHideAll: () => void;
  onShowAll: () => void;
  onToolClick: (tool: AssistantTool) => void;
}

const GROUP_ORDER: AssistantToolGroupLabel[] = ['Custom Tools', 'Actions', 'Insights', 'Internal'];

export const AssistantToolToolbar: React.FC<AssistantToolToolbarProps> = ({
  routeLabel,
  tools,
  isCollapsed,
  hiddenAll,
  onToggleCollapsed,
  onHideTool,
  onHideAll,
  onShowAll,
  onToolClick,
}) => {
  if (tools.length === 0 && !hiddenAll) {
    return null;
  }

  const groupedTools = GROUP_ORDER
    .map((label) => ({
      label,
      items: tools.filter((tool) => tool.groupLabel === label),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <section
      className="rounded-[20px] border border-primary/10 bg-[linear-gradient(180deg,rgba(246,250,248,0.98),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_14px_34px_rgba(28,59,62,0.08)]"
      data-testid="ai-tool-toolbar"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Wrench size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/70">
                Assistant Tools
              </p>
              <p className="mt-1 text-[13px] font-semibold leading-5 text-slate-900">
                {hiddenAll
                  ? `Tools for ${routeLabel} are hidden for this account.`
                  : `${tools.length} available tools are ready for ${routeLabel}.`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="rounded-full border border-primary/15 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/5"
                onClick={hiddenAll ? onShowAll : onToggleCollapsed}
                data-testid="ai-tool-toolbar-toggle"
              >
                {hiddenAll ? 'Show tools' : isCollapsed ? (
                  <span className="inline-flex items-center gap-1">
                    <ChevronDown size={14} />
                    Expand
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <ChevronUp size={14} />
                    Collapse
                  </span>
                )}
              </button>
              {!hiddenAll ? (
                <button
                  type="button"
                  className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
                  onClick={onHideAll}
                  data-testid="ai-tool-toolbar-hide-all"
                >
                  Hide all
                </button>
              ) : null}
            </div>
          </div>

          {hiddenAll ? (
            <p className="mt-3 text-[11px] leading-5 text-irongrey/75">
              Restore the toolbar any time to keep custom tools and route-aware actions visible while you chat.
            </p>
          ) : null}

          {!hiddenAll && !isCollapsed ? (
            <div className="mt-3 space-y-3">
              {groupedTools.map((group) => (
                <div key={group.label} className="space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
                    {group.label}
                  </p>
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
                    }}
                  >
                    {group.items.map((tool) => (
                      <div
                        key={tool.toolId}
                        className={[
                          'group relative overflow-hidden rounded-[20px] border px-3 py-3 shadow-[0_12px_28px_rgba(28,59,62,0.06)]',
                          tool.promotion === 'promoted'
                            ? 'border-primary/15 bg-[linear-gradient(180deg,rgba(236,246,244,0.98),rgba(255,255,255,0.98))]'
                            : 'border-primary/10 bg-white/95',
                        ].join(' ')}
                        data-testid={`ai-tool-toolbar-item-${tool.toolId}`}
                        data-promotion={tool.promotion}
                      >
                        <span className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1 basis-0">
                            <button
                              type="button"
                              className="block w-full text-left"
                              onClick={() => onToolClick(tool)}
                              data-testid={`ai-tool-toolbar-action-${tool.toolId}`}
                              title={tool.prompt ?? tool.summary}
                            >
                              <div className="flex items-center gap-2">
                                {tool.promotion === 'promoted' ? (
                                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Sparkles size={14} />
                                  </span>
                                ) : null}
                                <span className="truncate text-[12px] font-semibold text-slate-900">
                                  {tool.title}
                                </span>
                                {tool.requiresConfirmation ? (
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                    Confirm
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-1 text-[11px] leading-5 text-irongrey/75">
                                {tool.summary}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-primary/10 bg-primary/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/75">
                                  {group.label}
                                </span>
                                {tool.executionPlane === 'async_task' ? (
                                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-irongrey/60">
                                    Background
                                  </span>
                                ) : null}
                                {tool.targetRoute ? (
                                  <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-irongrey/60">
                                    Route linked
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#f6f8f7] px-2 py-1 text-[10px] font-semibold text-primary/80">
                              Open
                              <ArrowUpRight size={12} />
                            </span>
                            <button
                              type="button"
                              className="shrink-0 rounded-full border border-slate-200 p-1.5 text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600"
                              onClick={() => onHideTool(tool.toolId)}
                              aria-label={`Hide ${tool.title}`}
                              data-testid={`ai-tool-toolbar-hide-${tool.toolId}`}
                              title="Hide this tool"
                            >
                              <EyeOff size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
